import React, { createContext, useState, useContext, ReactNode } from 'react';
import en_GB from '../Locales/en-GB.json';
import en_US from '../Locales/en-US.json';
import de from '../Locales/de.json';
import 'dayjs/locale/en';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/de';
import { convert } from 'convert';

export type Language = 'en-GB' | 'en-US' | 'de';
export const LANGUAGES = new Set<Language>(['en-GB', 'en-US', 'de']);

interface Translations {
  [key: string]: string;
}

interface TranslationContextType {
  t: (key: string, values?: Record<string, string>) => string;
  language: Language;
  changeLanguage: (language: Language) => void;
  setDefaultLanguage: (language: Language) => void;
  distance: DistanceStatic;
  temperature: Temperature;
}

const translations: Record<Language, Translations> = {
  'en-GB': en_GB,
  'en-US': en_US,
  'de': de,
};

export interface DistanceStatic {
  show(
    distance_mm: number,
    options?: ShowDistanceOptions<'metric' | 'imperial'>,
  ): string;
  to_metric(distance: number): number;
  from_metric(distance_mm: number): number;
  from(distance: string): number;
}
type ShowDistanceOptions<K extends 'metric' | 'imperial'> = {
  unit: K extends 'metric' ? 'mm' | 'cm' : 'in' | 'ft-in';
};

abstract class Distance {
  static show(
    _distance_mm: number,
    _options?: ShowDistanceOptions<'metric' | 'imperial'>,
  ): string {
    throw new Error('Not implemented.');
  }
  static to_metric(_distance: number): number {
    throw new Error('Not implemented.');
  }
  static from_metric(_distance_mm: number): number {
    throw new Error('Not implemented.');
  }
  static from(_distance: string): number {
    throw new Error('Not implemented.');
  }
}

export class MetricDistance extends Distance {
  static show(
    distance_mm: number,
    options: ShowDistanceOptions<'metric'> = { unit: 'mm' },
  ): string {
    const converted = convert(distance_mm, 'mm').to(options.unit);
    const formatter = new Intl.NumberFormat('en-GB', {
      maximumFractionDigits: options.unit === 'mm' ? 0 : 1,
      style: 'unit',
      unit: options.unit === 'mm' ? 'millimeter' : 'centimeter',
      unitDisplay: 'narrow',
    });
    return `${formatter.format(converted)}`;
  }
  static from(distance: string): number {
    return Number(distance);
  }
  static to_metric(distance: number): number {
    return distance;
  }
  static from_metric(distance_mm: number): number {
    return distance_mm;
  }
}

export class ImperialDistance extends Distance {
  private static inchesFormatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 1,
    style: 'unit',
    unit: 'inch',
    unitDisplay: 'narrow',
  });
  private static feetFormatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
    style: 'unit',
    unit: 'foot',
    unitDisplay: 'narrow',
  });

  static show(
    distance_mm: number,
    options?: ShowDistanceOptions<'imperial'>,
  ): string {
    const distance_inches = convert(distance_mm, 'mm').to('in');
    const rounded_distance_inches = Math.round(distance_inches);
    const distance_ft = Math.floor(rounded_distance_inches / 12);
    if (options?.unit === 'in' || distance_ft < 1) {
      return `${this.inchesFormatter.format(distance_inches)}`;
    }

    return `${this.feetFormatter.format(distance_ft)} ${this.inchesFormatter.format(rounded_distance_inches % 12)}`;
  }
  static from(distance: string): number {
    return convert(Number(distance), 'in').to('mm');
  }
  static to_metric(distance: number): number {
    return convert(distance, 'in').to('mm');
  }
  static from_metric(distance_mm: number): number {
    return convert(distance_mm, 'mm').to('in');
  }
}

interface Temperature {
  show(temp_c: number): string;
  from(temp: string): number;
}

class Celsius implements Temperature {
  private formatter = new Intl.NumberFormat('en-GB', {
    maximumFractionDigits: 1,
    style: 'unit',
    unit: 'celsius',
    unitDisplay: 'short',
  });

  show(temp_c: number): string {
    return `${this.formatter.format(temp_c)}`;
  }
  from(temp: string): number {
    return Number(temp);
  }
}

class Farenheit implements Temperature {
  private formatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 1,
    style: 'unit',
    unit: 'fahrenheit',
    unitDisplay: 'short',
  });

  show(temp_c: number): string {
    return `${this.formatter.format(convert(temp_c, 'C').to('F'))}`;
  }
  from(temp: string): number {
    return convert(Number(temp), 'F').to('C') as number;
  }
}

const TranslationContext = createContext<TranslationContextType | undefined>(
  undefined,
);

export const LANGUAGE_STORAGE_KEY = 'language';
export const LAST_ACCOUNT_STORAGE_KEY = 'lastAccountId';

function getLanguageFromLocalStorage() {
  const languageFromLocalstorage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (!languageFromLocalstorage) {
    return null;
  }

  const parsedLanguage: Language = JSON.parse(languageFromLocalstorage);
  if (!LANGUAGES.has(parsedLanguage)) {
    return null;
  }

  return parsedLanguage;
}

const celsius = new Celsius();
const farenheit = new Farenheit();

const distanceMap: Record<Language, DistanceStatic> = {
  'en-GB': MetricDistance,
  'en-US': ImperialDistance,
  'de': MetricDistance,
};

const temperatureMap = {
  'en-GB': celsius,
  'en-US': farenheit,
  'de': celsius,
};

export const TranslationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [language, setLanguage] = useState<Language>(
    () => getLanguageFromLocalStorage() || 'en-GB',
  );

  const t = (key: string, values?: Record<string, string>): string => {
    let translation = translations[language][key] || key;
    if (values) {
      for (const placeholder in values) {
        translation = translation.replaceAll(
          `{${placeholder}}`,
          values[placeholder],
        );
      }
    }
    return translation;
  };

  const changeLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, JSON.stringify(lang));
  };

  /**
   * Sets the default language if it is not already set in local storage.
   */
  function setDefaultLanguage(language: Language) {
    if (getLanguageFromLocalStorage()) {
      return;
    }

    changeLanguage(language);
  }

  return (
    <TranslationContext.Provider
      value={{
        t,
        language,
        changeLanguage,
        setDefaultLanguage,
        distance: distanceMap[language],
        temperature: temperatureMap[language],
      }}
    >
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = (): TranslationContextType => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};
